/**
 * Central registry of play modes and visibilities in the unified
 * instance-first play flow.
 *
 * "Mode" = what is being played (Match / Sandbox / Draft / Sealed).
 * "Visibility" = who can join (Solo / Friends / Public).
 *
 * The two combine multiplicatively via getModesForVisibility. Solo
 * is not compatible with Draft or Sealed because they require 2+
 * seats and have no single-player meaning.
 *
 * Spec:
 * docs/superpowers/specs/2026-04-24-unified-instance-save-system-design.md
 */
import { formatRank } from './rankUtils';
import { getPref, setPref } from '../persistentPrefs';
import { k } from '../../localization/t';
import { isFreePlay } from '../freePlay';

function rankBadge(profile) {
  const rank = profile?.rank;
  if (!rank?.tier) return { label: k('Rank'), value: k('Unranked') };
  return { label: k('Rank'), value: formatRank(rank.tier, rank.division) };
}

// Each mode also carries UI hints (title/tagline/description/heroArt)
// so ModeTile can render it without a separate copy table. Solo
// excludes drafts and sealed. The allowedVisibilities array is the
// single source of truth for what the mode picker renders per
// visibility.
export const GAME_MODES = [
  {
    key: 'match',
    title: k('Match'),
    tagline: k('1v1 against another player.'),
    description: k('A standard one-on-one match. Ranked results move your Legend Points and the leaderboard; Casual results stay off the ladder but still pay out coins, XP and shards.'),
    heroArt: '/play/ranked.webp',
    order: 10,
    // No solo. There's no opponent. Use Sandbox for a single-
    // player local table.
    allowedVisibilities: ['friends', 'public'],
    supportsRanked: true,
    seatsMin: 2,
    seatsMax: 2,
    isAvailable: () => ({ available: true }),
    getBadges: (profile) => [rankBadge(profile)],
    // Free play pays nothing, so the copy can't promise rewards.
    freePlay: {
      description: k('A standard one-on-one match with any deck. Ranked results move your Legend Points and the leaderboard; Casual results stay off the ladder.'),
    },
  },
  {
    key: 'sandbox',
    title: k('Sandbox'),
    tagline: k('Any deck, no rewards.'),
    description: k('Play freely with any deck. Legality checks are off and cards you do not own are allowed. Nothing is tracked. Great for brewing and experimenting before committing to a ladder deck.'),
    heroArt: '/play/sandbox.webp',
    order: 20,
    allowedVisibilities: ['solo', 'friends', 'public'],
    supportsRanked: false,
    seatsMin: 2,
    seatsMax: 2,
    isAvailable: () => ({ available: true }),
    getBadges: () => [{ label: k('Rewards'), value: k('None') }],
    // Free play has no Sandbox: Match accepts any deck. The mode lives on
    // only as the engine behind the Solo table, named for what it is there.
    freePlay: {
      allowedVisibilities: ['solo'],
      title: k('Solo Table'),
      tagline: k('Any decks, both seats.'),
      description: k('Play both sides yourself with any decks. Nothing is tracked. Great for playtesting matchups or practicing opening lines.'),
      getBadges: () => [],
    },
  },
  {
    key: 'draft',
    title: k('Draft'),
    tagline: k('Pod, pick, battle.'),
    description: k('Gather 4 to 8 players, pass booster packs around to build a deck on the spot, then play a Swiss tournament together.'),
    heroArt: '/play/draft.webp',
    order: 30,
    allowedVisibilities: ['friends', 'public'],
    supportsRanked: false,
    seatsMin: 4,
    seatsMax: 8,
    isAvailable: () => ({ available: true }),
    getBadges: () => [],
  },
  {
    key: 'sealed',
    title: k('Sealed'),
    tagline: k('Open six packs, build, play.'),
    description: k('Each participant opens six booster packs, builds a deck using only what they opened, and plays the pod.'),
    heroArt: '/play/sealed.webp',
    order: 40,
    allowedVisibilities: ['friends', 'public'],
    supportsRanked: false,
    seatsMin: 2,
    seatsMax: 8,
    isAvailable: () => ({ available: true }),
    getBadges: () => [],
  },
];

// Visibility tiles rendered on the SessionBrowser as the "Create
// New Instance" strip. Shares the ModeTile shape (title / tagline /
// description / heroArt) so the tile component can render either.
export const VISIBILITY_TILES = [
  {
    key: 'solo',
    title: k('Solo'),
    tagline: k('Just you.'),
    description: k('Play both sides on your own machine. Nothing online, nothing tracked. Great for playtesting matchups or practicing opening lines.'),
    heroArt: '/play/casual.webp',
    order: 10,
  },
  {
    key: 'bot',
    title: k('vs Bot'),
    tagline: k('Face the Geomancer.'),
    description: k('Practice against the Geomancer starter deck with automated rules. Experimental opponent. Casual rewards, no rank.'),
    heroArt: '/play/casual.webp',
    order: 15,
    // Rendered only by isPracticeBotAvailable() (dev build on the local
    // server, or the beta channel); the server refuses botOpponent outright
    // on the stable production service.
    practiceBot: true,
  },
  {
    key: 'friends',
    title: k('Friends'),
    tagline: k('Invite who you want.'),
    description: k('Create a private instance and invite friends by name or share a join code. Only the people you invite can participate.'),
    heroArt: '/play/friend-match.webp',
    order: 20,
  },
  {
    key: 'public',
    title: k('Public'),
    tagline: k('Anyone can join.'),
    description: k('Create a public instance. It appears in the browser for any player to join while seats remain.'),
    heroArt: '/play/ranked.webp',
    order: 30,
  },
];

/**
 * A mode as the player should see it right now: in free play, with its
 * `freePlay` overrides applied. Read at render time, never at module scope.
 */
export function presentMode(mode) {
  if (!mode?.freePlay || !isFreePlay()) return mode;
  const { freePlay, ...base } = mode;
  return { ...base, ...freePlay };
}

/** The registry entry for a mode key, as presented right now. */
export function getGameMode(key) {
  return presentMode(GAME_MODES.find((m) => m.key === key));
}

/**
 * Stakes label for an instance row. 'Ranked' or 'Casual'. Only Match
 * instances carry stakes; every other mode says what it pays in its own
 * name (Sandbox pays nothing, Draft / Sealed are Limited), so they get
 * null and callers render nothing.
 *
 * Ranked and Casual pay identical coins / XP / shards; the only
 * difference is whether the result moves the leaderboard. Every surface
 * that shows one must show the other, or an opted-out match reads as
 * "no stakes at all." The confusion behind issue #41.
 */
export function stakesLabel(mode, rankedOptIn) {
  if (mode !== 'match') return null;
  return rankedOptIn ? k('Ranked') : k('Casual');
}

// NOTE a DIFFERENT vocabulary from the GAME_MODES keys above. Those name
// the instance a player creates ('match', 'sandbox', 'draft', 'sealed');
// these name how the finished match was scored, which is what gets
// persisted per participant. 'limited' covers both draft and sealed
// rounds, and the ranked/casual split only exists after the fact.
const MATCH_MODE_LABELS = {
  ranked: k('Ranked'),
  casual: k('Casual'),
  // Draft and sealed rounds both persist as 'limited', so the two cannot be
  // told apart after the fact.
  limited: k('Limited'),
  sandbox: k('Sandbox'),
  // One pilot on both seats. Rows from before solo had its own bucket
  // say 'sandbox'; ReplayShareCard tells them apart by the seats.
  solo: k('Solo'),
};

/**
 * Human label for a `match_history.mode` value, or null when the mode is
 * absent or unknown, so callers render nothing rather than a raw column
 * value.
 */
export function matchModeLabel(mode) {
  return MATCH_MODE_LABELS[mode] || null;
}

/**
 * Returns the modes playable at a given visibility (solo / friends
 * / public). Used by the mode picker sub-screen to filter which
 * tiles to render.
 */
export function getModesForVisibility(visibility) {
  return GAME_MODES
    .map(presentMode)
    .filter((m) => m.allowedVisibilities.includes(visibility))
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * Returns all modes paired with their availability, sorted by
 * ascending order. Used by the mode picker to render the full grid
 * while letting the caller dim unavailable entries.
 */
export function getVisibleModes(profile) {
  return GAME_MODES.map(presentMode)
    .map((mode) => ({
      mode,
      availability: mode.isAvailable?.(profile) || { available: true },
    }))
    .sort((a, b) => (a.mode.order || 0) - (b.mode.order || 0));
}

/**
 * Per-mode memory of the last confirmed setup-dialog options, so
 * starting the same mode again reopens with the choices the player
 * made last time instead of the cold defaults.
 *
 * Callers own validation: a stored value can be stale (a booster set
 * that is no longer unlocked, a pod size that was disabled since),
 * so every restored field must be checked against the options the
 * dialog actually offers before it lands in state.
 */
export function loadModeSetup(modeKey) {
  const saved = modeKey ? getPref(`vk-mode-setup-${modeKey}`, null) : null;
  return saved && typeof saved === 'object' ? saved : {};
}

export function saveModeSetup(modeKey, values) {
  if (modeKey) setPref(`vk-mode-setup-${modeKey}`, values);
}
